// React components
import { useState, useEffect, useContext } from "react";
import partCodesService from "services/part-code-service";
import partCategoryService from "services/part-category-service";
import changeLogService from "services/change-log-service";
import { AuthContext } from "context";
import formatPartCode from "helper/formatPartCode";

// MUI and Material Dashboard Components
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormField from "layouts/applications/wizard/components/FormField";
import Button from '@mui/material/Button';
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import CircularProgress from '@mui/material/CircularProgress';

export default function AddToAssembly({
    handleClose,
    open,
    id
}) {
    const [partCategories, setPartCategories] = useState([]); // Stores fetched Part Categories
    const [selectedPartCategory, setSelectedPartCategory] = useState(""); // Stores the selected partCategory
    const [partCodes, setPartCodes] = useState([]); // Stores fetched Part Codes
    const [selectedPartCode, setSelectedPartCode] = useState(""); // Stores the selected partCode
    const [filteredPartCodes, setFilteredPartCodes] = useState([]); // Stores part codes filtered by part category
    const [isLoading, setIsLoading] = useState(false); // Loading state

    const authContext = useContext(AuthContext);

    // Additional data : Notes and Quantity
    const [additionalData, setAdditionalData] = useState({
        text: "",
        quantity: 1
    });

    // Fetches the part categories and part codes
    useEffect(() => {
        (async () => {
            try {
            setIsLoading(true);
            const response1 = await partCategoryService.getAllPartCategories();
            setPartCategories(response1.data.attributes.filter(category => category.parentCategory === 'ASSEMBLIES'));
            const reponse2 = await partCodesService.getParts();
            const approvedParts = reponse2.data.attributes.filter(part => part.status === "Approved");
            setPartCodes(approvedParts);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Handles Part Code change
    useEffect(() => {
        if (selectedPartCategory) {
            const filterparts = partCodes.filter((part) => part.partCategory.categoryCode === selectedPartCategory.categoryCode);
            const formattedPartCodes = filterparts.map((part) => ({
                ...part,
                partCode: formatPartCode(part)
            }));
            setFilteredPartCodes(formattedPartCodes);
        } else {
            // Empties the fields related to partCategory if no partCategory is selected
            setFilteredPartCodes([]);
        }
        setSelectedPartCode("");
        //changeHandler({ target: { name: "partCategory.categoryCode", value: selectedPartCategory } });
    }, [selectedPartCategory]);

    // Change Handler for the additional notes and quantity fields
    const changeHandler = (e) => {
        setAdditionalData({
            ...additionalData,
            [e.target.name]: e.target.value
        });
    }

    const commonProps = { // Props common for the input fields
        display: "flex",
        flexDirection: "column",
        fullWidth: true,
        marginBottom: "1rem",
        marginTop: "1rem"
    };

    // Adds the partcode to the selected assembly
    const handleAdd = async () => {
        const selectedAssembly = await partCodesService.getChildren(selectedPartCode._id);
        const payload = {
            data: {
                type: "PartCode",
                id: selectedPartCode._id,
                attributes: {
                    children: [
                        ...selectedAssembly.data.attributes.children,
                        {
                            part: id,
                            text: additionalData.text,
                            Quantity: additionalData.quantity
                        }
                    ]
                }
            }

        }
        await partCodesService.updateChildren(payload, selectedPartCode._id);

        // Change Log Entry
        const changeLog = {
            data: {
                attributes: {
                    partCode: id,
                    change: {
                        type: "Added to BOM",
                        description: `Added to ${selectedPartCode.partCode} `,
                        date: new Date(), // Current Date
                        user: await authContext.getCurrentUser()
                    }
                }
            }
        };

        await changeLogService.addChange(changeLog);
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Select a Part Code to add</DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </MDBox>
                ) : (
                <MDBox margin={2}>

                    {/* Parent Category */}
                    <MDBox {...commonProps}>
                        <MDInput
                            label="Parent Catregory"
                            name="parentCategory"
                            value="Assemblies"
                            fullWidth
                            disabled
                        />
                    </MDBox>

                    {/* Part Category*/}
                    <MDBox {...commonProps}>
                        <Autocomplete
                            defaultValue=""
                            options={partCategories}
                            getOptionLabel={(option) => (option ? option.categoryCode : "")}
                            value={selectedPartCategory ?? ""}
                            onChange={(event, newValue) => {
                                setSelectedPartCategory(newValue);
                            }}
                            renderInput={(params) => (
                                <FormField {...params} label="Part Category" InputLabelProps={{ shrink: true }} />
                            )} />
                    </MDBox>


                    {/* Part Code */}
                    <MDBox {...commonProps}>
                        <Autocomplete
                            defaultValue=""
                            options={filteredPartCodes}
                            getOptionLabel={(option) => (option ? option.partCode : "")}
                            value={selectedPartCode ?? ""}
                            onChange={(event, newValue) => {
                                setSelectedPartCode(newValue);
                            }}
                            renderInput={(params) => (
                                <FormField {...params} label="Part Code" InputLabelProps={{ shrink: true }} />
                            )} />
                    </MDBox>

                    {/* Additional Notes Field */}
                    <MDBox {...commonProps}>
                        <MDInput
                            label="Additional Notes"
                            name="text"
                            value={additionalData.text}
                            onChange={changeHandler}
                            variant="standard"
                            fullWidth
                        />
                    </MDBox>

                    {/* Quantity Field */}
                    <MDBox {...commonProps}>
                        <MDInput
                            label="Quantity"
                            name="quantity"
                            type="number"
                            value={additionalData.quantity}
                            onChange={changeHandler}
                            variant="standard"
                            fullWidth
                        />
                    </MDBox>

                </MDBox>
                )}
            </DialogContent>

            <DialogActions>

                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleAdd}>Add</Button>

            </DialogActions>

        </Dialog>
    )
}
